import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaWhatsapp,
  FaLinkedin,
} from "react-icons/fa";
import { MapPinned, Mail, Phone } from "lucide-react";

const FooterWrapper = styled.footer`
  background: linear-gradient(180deg, #020510 0%, #0a0e1f 100%);
  color: white;
  padding: 70px 20px 20px;
  font-family: "Poppins", sans-serif;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
  box-sizing: border-box;
  position: relative;
  border-top: 2px solid transparent;
  border-image: linear-gradient(90deg, transparent, #0047ff 20%, #00c4ff 50%, #0047ff 80%, transparent) 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(0, 196, 255, 0.5) 50%,
      transparent 100%
    );
  }
`;

const Column = styled.div`
  flex: 1 1 250px;
  min-width: 200px;
`;

const Title = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 18px;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.5px;
  position: relative;
  padding-bottom: 10px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, #0047ff, #00c4ff);
    border-radius: 2px;
  }
`;

const StyledLink = styled(Link)`
  display: block;
  color: #94a3b8;
  text-decoration: none;
  margin-bottom: 10px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;

  &:hover {
    color: #00c4ff;
    padding-left: 8px;
  }

  &::before {
    content: '→';
    position: absolute;
    left: -20px;
    opacity: 0;
    transition: all 0.3s ease;
  }

  &:hover::before {
    left: -2px;
    opacity: 1;
  }
`;

const Text = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.9;
  margin-bottom: 20px;
`;

const FooterNote = styled.div`
  text-align: center;
  width: 100%;
  padding-top: 35px;
  font-size: 0.85rem;
  color: #64748b;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 40px;
  letter-spacing: 0.3px;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 20px;
  justify-content: center;
`;

const SocialLink = styled.a`
  background: linear-gradient(135deg, rgba(0, 71, 255, 0.15), rgba(0, 196, 255, 0.15));
  color: #00c4ff;
  font-size: 1.3rem;
  width: 45px;
  height: 45px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 196, 255, 0.2);
  transition: all 0.35s ease;
  backdrop-filter: blur(8px);

  &:hover {
    background: linear-gradient(135deg, #0047ff, #00c4ff);
    border-color: #00c4ff;
    color: #fff;
    transform: scale(1.15) rotate(5deg);
    box-shadow: 0 12px 32px rgba(0, 196, 255, 0.4);
  }
`;
const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  font-size: 0.9rem;
  color: #94a3b8;
  text-align: left;
  transition: all 0.3s ease;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: #0047ff;
    padding: 6px;
    background: rgba(0, 71, 255, 0.1);
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  &:hover svg {
    background: rgba(0, 196, 255, 0.2);
    color: #00c4ff;
    transform: scale(1.1);
  }

  a {
    color: #94a3b8;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #00c4ff;
    }
  }
`;

export default function Footer() {
  return (
    <FooterWrapper>

      <Column>
        <Title>Berrybloomer</Title>
        <Text>
          Empowering businesses through tech innovation and smart consulting. We
          help you grow online.
        </Text>

        <SocialIcons>
          <SocialLink href="https://wa.me/918905578564" target="_blank" rel="noopener noreferrer">
            <FaWhatsapp />
          </SocialLink>

          {/* <SocialLink href="https://twitter.com/yourprofile" target="_blank" rel="noopener noreferrer">
            <FaTwitter />
          </SocialLink> */}

          <SocialLink href="https://www.instagram.com/berrybloomer.tech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </SocialLink>

          {/* <SocialLink href="https://youtube.com/yourchannel" target="_blank" rel="noopener noreferrer">
            <FaYoutube />
          </SocialLink> */}

          <SocialLink href="https://www.linkedin.com/company/berrybloomer-pvt-ltd-it/" target="_blank" rel="noopener noreferrer">
            <FaLinkedin />
          </SocialLink>
        </SocialIcons>
      </Column>

      <Column>
        <Title>Quick Links</Title>
        <StyledLink to="/">Home</StyledLink>
        <StyledLink to="/android">Android</StyledLink> {/* ✅ FIX */}
        <StyledLink to="/ios">Ios</StyledLink>
        <StyledLink to="/pages/contact">Contact</StyledLink>
      </Column>

      <Column>
        <Title>Contact</Title>

        <ContactItem>
          <MapPinned size={18} />
          <span>Bharatpur, India</span>
        </ContactItem>

        <ContactItem>
          <Mail size={18} />
          <a href="mailto:khushboo.sharma@berrybloomer.com">
            khushboo.sharma@berrybloomer.com
          </a>
        </ContactItem>

        <ContactItem>
          <Phone size={18} />
          <a href="tel:+917976160206">
            +91 7976160206
          </a>
        </ContactItem>

        <ContactItem>
          <Phone size={18} />
          <a href="tel:+918377835702">
            +91 8377835702
          </a>
        </ContactItem>

      </Column>

      <FooterNote>
        © {new Date().getFullYear()} Berrybloomer. All rights reserved.
      </FooterNote>

    </FooterWrapper>
  );
}